import { useCallback } from "react";
import { flushSync } from "react-dom";
import { useMediaQuery } from "usehooks-ts";

/** `document.startViewTransition` is not in every engine's lib.dom yet. */
type TViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => { finished: Promise<void> };
};

/**
 * Animates the grid re-flow that follows a layout change.
 *
 * CSS Grid cannot tween `grid-column` / `grid-row`, so resizing or removing a
 * widget used to teleport every card after it to its new slot in a single frame.
 * dnd-kit covers the *dragged* item and nothing else, which left the most common
 * edits — resize, remove, reset — as hard cuts.
 *
 * The View Transitions API is the only mechanism that animates between two
 * layouts the browser computed independently: it snapshots every element with a
 * `view-transition-name`, applies the DOM change, then tweens each snapshot from
 * its old box to its new one. That is exactly the missing piece, and it needs no
 * bookkeeping of positions on our side.
 *
 * `flushSync` is required: the callback must leave the DOM in its final state
 * before it returns, and React would otherwise batch the update past that point.
 *
 * Degrades to a plain call — same result, no animation — where the API is absent
 * (Firefox at time of writing) or motion is turned down.
 */
export function useViewTransition() {
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );

  return useCallback(
    (update: () => void) => {
      const startViewTransition = (document as TViewTransitionDocument)
        .startViewTransition;

      if (prefersReducedMotion || typeof startViewTransition !== "function") {
        update();
        return;
      }

      startViewTransition.call(document, () => flushSync(update));
    },
    [prefersReducedMotion],
  );
}
